import { toWSDateString } from "@wingspanhq/fe-component-library";
import { Address } from "@wingspanhq/wingspan-api-sdk";

import { PAYROLL_RUNS_DEFAULT_YEAR } from "./constants";
import { PayrollRunsYear } from "./types";

export const getDefaultPayrollRunYear = (): PayrollRunsYear => {
  return PAYROLL_RUNS_DEFAULT_YEAR;
};

/**
 * The `filter.payDate` range for a dashboard year — the v3 payroll list has no
 * "year" filter, so we bound the pay date to the calendar year.
 *
 * As of SDK 1.12.2 the list query params run through the model `ToJSON`, and the
 * `payDate` `ToJSON` calls `value.toISOString()` — so the field takes a `Date`
 * and serializes to a full ISO datetime (a string would throw). We build the
 * boundaries at UTC midnight so the serialized value is deterministic across
 * timezones (`${year}-01-01T00:00:00.000Z` … `${year}-12-31T00:00:00.000Z`).
 */
export const getPayrollRunYearDateRange = (
  year: PayrollRunsYear
): { gte: Date; lte: Date } => ({
  gte: new Date(`${year}-01-01T00:00:00.000Z`),
  lte: new Date(`${year}-12-31T00:00:00.000Z`)
});

/** Card-header label from the pay period start, e.g. "January 2024". */
export const formatPayPeriodLabel = (start?: Date): string => {
  if (!start) return "—";
  return `${toWSDateString(start, "month")} ${toWSDateString(start, "year")}`;
};

/** Human-readable pay period range, e.g. "Jan 1, 2024 - Jan 31, 2024". */
export const formatPayPeriodRange = (start?: Date, end?: Date): string => {
  if (!start || !end) return "—";
  return `${toWSDateString(start, "monthDayYear")} - ${toWSDateString(
    end,
    "monthDayYear"
  )}`;
};

/** v3 money amounts arrive as decimal-dollar strings; coerce for formatMoney. */
export const parsePayrollAmount = (amount?: string | number | null): number =>
  Number(amount ?? 0);

/** One-line display string for a v3 `Address` (empty when there's nothing). */
export const formatV3Address = (address?: Address | null): string => {
  if (!address) return "";
  const cityStateZip = [
    address.city ? `${address.city},` : undefined,
    address.state,
    address.postalCode
  ]
    .filter(Boolean)
    .join(" ");
  return [address.line1, address.line2, cityStateZip]
    .filter(Boolean)
    .join(", ");
};
